import { api } from "../axios";
import { DistributorEmployee, PaginatedResponse } from "../../types";

export type DistributorEmployeeRole = "admin" | "employee";

export interface CreateDistributorEmployeePayload {
  username: string;
  role_id: number;
  status?: "active" | "verification_pending" | "deactivated";
  email: string;
  phone_number: string;
  euin?: string | null;
  sif_euin?: string | null;
  name: string;
}

export interface ListDistributorEmployeesParams {
  page?: number;
  page_size?: number;
  /** Filter by distributor ID */
  distributor?: number;
  /** Filter by role (admin, employee) */
  role?: DistributorEmployeeRole;
  /**
   * Filter by employee status. Single value or comma-separated values
   * (e.g. `"active,verification_pending"`).
   */
  status?: string;
}

export function listDistributorEmployees(
  params?: ListDistributorEmployeesParams
): Promise<PaginatedResponse<DistributorEmployee>> {
  return api.get("distributor/employees/", { params });
}

export function createDistributorEmployee(
  payload: CreateDistributorEmployeePayload
): Promise<DistributorEmployee> {
  return api.post("distributor/employees/", payload);
}

export function getDistributorEmployeeById(
  id: number
): Promise<DistributorEmployee> {
  return api.get(`distributor/employees/${id}/`);
}

export interface UpdateDistributorEmployeePayload {
  username: string;
  role_id: number;
  status?: "active" | "verification_pending" | "deactivated";
  email: string;
  phone_number: string;
  euin?: string | null;
  sif_euin?: string | null;
  name: string;
}

export function updateDistributorEmployee(
  id: number,
  payload: UpdateDistributorEmployeePayload
): Promise<DistributorEmployee> {
  return api.put(`distributor/employees/${id}/`, payload);
}

export function deleteDistributorEmployee(id: number): Promise<void> {
  return api.delete(`distributor/employees/${id}/`);
}

/** Resends the onboarding invite; backend expects the same body as update. */
export function resendDistributorEmployeeInvite(
  id: number,
  payload: UpdateDistributorEmployeePayload
): Promise<void> {
  return api.post(`distributor/employees/${id}/resend-invite-email/`, payload);
}

export interface UpdateEmployeeClientMappingPayload {
  employee_id: number | "";
  assigned_client_ids: number[];
  deassigned_client_ids: number[];
}

export interface UpdateEmployeeClientMappingResponse {
  access_all_clients: boolean;
  num_clients_assigned: number;
  client_ids: number[];
}

/**
 * Updates which clients a distributor employee has access to.
 * Backend applies mapping deltas for the provided employee.
 */
export function updateEmployeeClientMapping(
  payload: UpdateEmployeeClientMappingPayload
): Promise<UpdateEmployeeClientMappingResponse> {
  return api.post(`distributor/employees/update-client-mapping/`, payload);
}

export interface EmployeeRole {
  id: number;
  name: string;
  description: string;
  grant_all_permissions: boolean;
  created_at: string;
  updated_at: string;
}

export function listEmployeeRoles(): Promise<PaginatedResponse<EmployeeRole>> {
  return api.get("employee-roles/");
}
